import { get, remove, set } from 'lib/storage/isomorphicSessionStorage'

const GUEST_CHECKOUT_SESSION_KEY = 'guestCheckoutCartId'
const GUEST_CHECKOUT_ITEM_TYPES: ReadonlyArray<App.Checkout.AnyItemType> = ['bedbankHotel', 'hotel'] // Just LPC hotel in this case

export interface GuestCheckoutGuardInput {
  brand: string
  isBotTraffic: boolean
  isGoogleHotelAdsVerifier: boolean
  isLoggedIn: boolean
  isWalledGarden: boolean
  isMultiRoom: boolean
  utmBp?: string
  /** The full cart is required to reject mixed-product checkouts. */
  itemTypes: ReadonlyArray<App.Checkout.AnyItemType>
}

interface GuestCheckoutRestoreGuardInput {
  hasMatchingSessionCart: boolean
  isCartRestored: boolean
  isGuestCheckoutMode: boolean
  isEligiblePurchase: boolean
}

/**
 * Determines whether a purchase can use guest checkout. This intentionally
 * is available to every storefront and fails closed for unsupported checkout
 * item types.
 *
 * GuestCheckoutPage currently renders only the review, traveller details, and
 * payment sections for a single LPP/Bedbank or LPC hotel room. Before relaxing
 * this guard to allow more item types, rooms, or add-ons, update that page to
 * render every newly available section and add coverage for the expanded guest
 * journey.
 */
export function canUseGuestCheckout(input: GuestCheckoutGuardInput): boolean {
  // Currently, guest checkout only allows one supported hotel item and one room.
  const hasSingleSupportedItem = input.itemTypes.length === 1 &&
    input.itemTypes.every(itemType => GUEST_CHECKOUT_ITEM_TYPES.includes(itemType))

  return input.brand === 'luxuryescapes' &&
    !input.isBotTraffic &&
    !input.isGoogleHotelAdsVerifier &&
    !input.isLoggedIn &&
    !input.isWalledGarden &&
    !input.isMultiRoom &&
    input.utmBp === '3' && // GHA traffic only
    hasSingleSupportedItem
}

/**
 * Restored checkout routes must satisfy both the live rollout decision and the
 * original purchase constraints; client-side guest markers alone are not enough.
 */
export function canRestoreGuestCheckout(input: GuestCheckoutRestoreGuardInput): boolean {
  return input.isCartRestored &&
    input.isGuestCheckoutMode &&
    input.hasMatchingSessionCart &&
    input.isEligiblePurchase
}

/**
 * The sessionStorage cart ID lets the checkout auth gate admit only the cart
 * that started guest checkout after that cart has been restored into redux.
 */
export function setGuestCheckoutSessionCartId(cartId: string | null) {
  if (cartId) {
    set(GUEST_CHECKOUT_SESSION_KEY, cartId)
  } else {
    remove(GUEST_CHECKOUT_SESSION_KEY)
  }
}

export function hasGuestCheckoutSessionCartId(cartId: string | null): boolean {
  return !!cartId && get(GUEST_CHECKOUT_SESSION_KEY) === cartId
}

/** Whether this browser has a guest-checkout cart awaiting restoration. */
export function hasGuestCheckoutSession(): boolean {
  return typeof get(GUEST_CHECKOUT_SESSION_KEY) === 'string'
}
